import React, { useEffect, Component } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import HeroAlt from './components/HeroAlt';
import ValueProps from './components/ValueProps';
import AnalyticsSnapshot from './components/AnalyticsSnapshot';
import Solutions from './components/Solutions';
import Features from './components/Features';
import Platforms from './components/Platforms';
import Implementation from './components/Implementation';
import Pricing from './components/Pricing';
import About from './components/About';
import ChatDemo from './components/ChatDemo';
import DemoInterface from './components/DemoInterface';
import AIExamples from './components/AIExamples';
import Footer from './components/Footer';
import ContactUs from './components/ContactUs';
import { DashboardDemo } from './components/DashboardDemo';
import { FeaturesDemo } from './components/FeaturesDemo';
import CaseStudies from './components/CaseStudies';
import { initGA, trackPageView } from './services/analytics';
import GPTStorePitchPage from './pages/GPTStorePitchPage';
import { ArrowRightIcon, PhoneIcon } from 'lucide-react';
import Button from './components/Button';
import ThankYou from './components/ThankYou';
import PricingPage from './pages/PricingPage';
import SEOHead from './components/SEOHead';
import HebrewLandingPage from './platform/hebrew';
import GoogleTag from './components/GoogleTag';
import WhyIBuiltShopilots from './components/WhyIBuiltShopilots';
import BlogIndex from './blog';
import ConversationNotifier from './components/ConversationNotifier';
import WhatsAppWidget from './components/WhatsAppWidget';
import UseCases from './components/UseCases';
import { I18nProvider } from './i18n/I18nProvider';
import LanguageSwitcher from './components/LanguageSwitcher';
import { useTranslation } from './i18n/useTranslation';
import './i18n/i18n'; // Import i18n initialization
import DeckMC from './deck/mc/index';
import StartNowBanner from './components/StartNowBanner';
import ShopilotsWidget from './components/ShopilotsWidget';
// Page tracker component
const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
};
// SEO component to handle page-specific metadata
const PageSEO = () => {
  const location = useLocation();
  const path = location.pathname;
  let title = 'Shopilots | AI Shopping Advisors for Retailers';
  let description = 'Turn your product catalog into a conversational salesforce with AI shopping advisors that sell — not just chat. Deploy on your website, social media, WhatsApp, and ChatGPT. Call (914) 601-1670 today.';
  if (path === '/pricing') {
    title = 'Pricing Plans | Shopilots AI Shopping Advisors';
    description = 'Simple, transparent pricing that scales with your business. Choose from Free, Pro, and Business plans with a 1-month free trial on all plans. Call (914) 601-1670 for details.';
  } else if (path === '/case_studies' || path === '/inspiration') {
    title = 'Case Studies | Shopilots AI Shopping Advisors';
    description = 'Discover how leading retailers use Shopilots AI shopping advisors to increase conversions, boost average order value, and enhance customer experience. Call (914) 601-1670 to learn more.';
  } else if (path === '/gpt-store') {
    title = 'GPT Store Solutions | Shopilots AI Shopping Advisors';
    description = 'Bring your brand into the AI era with a custom GPT Store that showcases your products and converts browsers into buyers. Call (914) 601-1670 for a consultation.';
  } else if (path === '/contact') {
    title = 'Contact Us | Shopilots AI Shopping Advisors';
    description = 'Get in touch with our team to learn how Shopilots can help your retail business leverage AI for increased sales and customer satisfaction. Call (914) 601-1670 now.';
  } else if (path === '/blog') {
    title = 'AI Retail Blog | Shopilots AI Shopping Advisors';
    description = 'Latest insights, trends and strategies for implementing AI in retail and e-commerce to boost sales and enhance customer experience. Call (914) 601-1670 for AI retail solutions.';
  }
  return <SEOHead title={title} description={description} data-id="element-0" />;
};
// Main App Content Component that uses translations
const AppContent = () => {
  const {
    t
  } = useTranslation();
  return <div className="bg-background text-text min-h-screen" data-id="element-1">
      <GoogleTag data-id="element-2" />
      <ShopilotsWidget data-id="element-3" />
      <PageSEO data-id="element-4" />
      <PageTracker data-id="element-5" />
      <Navbar data-id="element-6" />
      <StartNowBanner data-id="element-7" />
      <Routes data-id="element-8">
        <Route path="/" element={<main data-id="element-10">
              <HeroAlt data-id="element-11" />
              <UseCases data-id="element-12" />
              <ValueProps data-id="element-13" />
              <Solutions data-id="element-14" />
              <Platforms data-id="element-15" />
              {/* GPT Stores Promo Banner */}
              <div className="bg-gradient-to-r from-indigo-100 to-purple-100 py-8 border-y border-indigo-200" data-id="element-16">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between" data-id="element-17">
                  <div className="mb-4 md:mb-0 md:mr-8" data-id="element-18">
                    <h3 className="text-2xl font-bold text-indigo-900 mb-2" data-id="element-19">
                      {t('sections.gptPromo.title')}
                    </h3>
                    <p className="text-indigo-700 max-w-2xl" data-id="element-20">
                      {t('sections.gptPromo.description')}
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4" data-id="element-21">
                    <a href="tel:+19146011670" className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 shadow-md hover:shadow-lg transition-all duration-300 font-medium whitespace-nowrap" data-id="element-22">
                      <PhoneIcon className="mr-2 h-4 w-4" data-id="element-23" />
                      {t('buttons.callUs')}
                    </a>
                    <Link to="/gpt-store" data-id="element-24">
                      <Button variant="outline" size="lg" className="whitespace-nowrap border-2 border-indigo-300 hover:border-indigo-400 text-indigo-700" data-id="element-25">
                        {t('buttons.learnMore')}{' '}
                        <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-26" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
              <AnalyticsSnapshot data-id="element-27" />
              <Pricing data-id="element-28" />
              <DemoInterface data-id="element-29" />
            </main>} data-id="element-9" />
        <Route path="/contact" element={<ContactUs data-id="element-31" />} data-id="element-30" />
        <Route path="/case_studies" element={<CaseStudies data-id="element-33" />} data-id="element-32" />
        <Route path="/inspiration" element={<CaseStudies data-id="element-35" />} data-id="element-34" />
        <Route path="/gpt-store" element={<GPTStorePitchPage data-id="element-37" />} data-id="element-36" />
        <Route path="/thank-you" element={<ThankYou data-id="element-39" />} data-id="element-38" />
        <Route path="/pricing" element={<PricingPage data-id="element-41" />} data-id="element-40" />
        <Route path="/hebrew" element={<HebrewLandingPage data-id="element-43" />} data-id="element-42" />
        <Route path="/blog/*" element={<BlogIndex data-id="element-45" />} data-id="element-44" />
        <Route path="/deck/mc" element={<DeckMC data-id="element-47" />} data-id="element-46" />
      </Routes>
      <Footer data-id="element-48" />
      <ConversationNotifier data-id="element-49" />
      <WhatsAppWidget data-id="element-50" />
      <LanguageSwitcher data-id="element-51" />
    </div>;
};
export function App() {
  // Initialize Google Analytics
  useEffect(() => {
    initGA();
  }, []);
  // Ensure page starts at the top when loaded
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <BrowserRouter data-id="element-52">
      <I18nProvider data-id="element-53">
        <AppContent data-id="element-54" />
      </I18nProvider>
    </BrowserRouter>;
}